import { CheckCircle, LoaderCircle, RefreshCw, Rocket, Undo2 } from 'lucide-react';

import { Button } from '@/_shared/components/ui/button';
import { Card, CardContent } from '@/_shared/components/ui/card';

import type { MeasurementPlanStatus } from '../types';

interface PlanActionsBarProps {
  status: MeasurementPlanStatus;
  onApprove: () => void;
  onRevertToDraft: () => void;
  onMarkVerified: () => void;
  onGeneratePrs: () => void;
  approvePending: boolean;
  revertPending: boolean;
  verifyPending: boolean;
  generatePending: boolean;
}

/**
 * Workflow action bar for MeasurementPlanDetail.
 *
 * Deliberately does NOT include Delete. The destructive Delete action lives
 * in the page header (top-right) so it is visually separated from the forward
 * workflow actions (Approve / Generate PRs / Revert / Verify). Replacing the
 * old inline two-step confirm with a Dialog also lets us drop the
 * `confirmAction` state that used to live here.
 */
export function PlanActionsBar({
  status,
  onApprove,
  onRevertToDraft,
  onMarkVerified,
  onGeneratePrs,
  approvePending,
  revertPending,
  verifyPending,
  generatePending,
}: PlanActionsBarProps): React.ReactElement {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-wrap gap-2">
          {status === 'draft' && (
            <Button size="sm" onClick={onApprove} disabled={approvePending}>
              {approvePending ? (
                <LoaderCircle size={14} className="mr-1 animate-spin" />
              ) : (
                <CheckCircle size={14} className="mr-1" />
              )}
              Approve plan
            </Button>
          )}

          {status === 'approved' && (
            <>
              <Button size="sm" onClick={onGeneratePrs} disabled={generatePending}>
                {generatePending ? (
                  <LoaderCircle size={14} className="mr-1 animate-spin" />
                ) : (
                  <Rocket size={14} className="mr-1" />
                )}
                {generatePending ? 'Starting…' : 'Generate PRs'}
              </Button>
              <Button size="sm" variant="outline" onClick={onRevertToDraft} disabled={revertPending}>
                <Undo2 size={14} className="mr-1" />
                Revert to draft
              </Button>
            </>
          )}

          {status === 'implemented' && (
            <Button size="sm" onClick={onMarkVerified} disabled={verifyPending}>
              {verifyPending ? (
                <LoaderCircle size={14} className="mr-1 animate-spin" />
              ) : (
                <CheckCircle size={14} className="mr-1" />
              )}
              Mark as verified
            </Button>
          )}

          {status === 'partial_failure' && (
            <>
              <Button size="sm" onClick={onGeneratePrs} disabled={generatePending}>
                {generatePending ? (
                  <LoaderCircle size={14} className="mr-1 animate-spin" />
                ) : (
                  <RefreshCw size={14} className="mr-1" />
                )}
                {generatePending ? 'Starting…' : 'Retry failed'}
              </Button>
              <Button size="sm" variant="outline" onClick={onRevertToDraft} disabled={revertPending}>
                <Undo2 size={14} className="mr-1" />
                Revert to draft
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
